/**
 * Central site data for the Standard Service Template.
 *
 * The template pages import these as ES module bindings — e.g.
 *   import { SITE, IMAGES, SERVICES } from "../lib/_data";
 *
 * That means we can't reassign the exports at runtime (ES modules are read-only
 * bindings). Instead, each export is a *mutable* object/array and we mutate it
 * in place via `setServiceData(config)`. Template components re-read the
 * fields every render, so the next render after mutation picks up new content.
 *
 * The shell (PublicServiceShell.jsx) calls `setServiceData` synchronously with
 * the backend-built `service_config` payload *before* rendering the page tree,
 * so the very first render already sees real data.
 */

// ---------- Mutable module-scope stores ----------
export const SITE = {
  brand: "Your Business",
  brandFull: "Your Business",
  brandTagline: "Local Home Services",
  phone: "(555) 000-0000",
  phoneHref: "tel:+15550000000",
  email: "hello@yourbusiness.com",
  emailHref: "mailto:hello@yourbusiness.com",
  hours: [
    { day: "Mon – Fri", time: "8:00 AM – 6:00 PM" },
    { day: "Saturday", time: "9:00 AM – 3:00 PM" },
    { day: "Sunday", time: "Closed" },
  ],
  areasBlurb: "Proudly serving your community",
  serviceAreas: ["Your City"],
  address: "Your City",
};

export const IMAGES = {
  homeHero: "",
  servicesHero: "",
  aboutHero: "",
  consultationHero: "",
  reviewsHero: "",
  faqHero: "",
  contactHero: "",
  finalCta: "",
  whyChooseUs: "",
  consultRoom: "",
};

export const SERVICES = [];
export const TESTIMONIALS = [];
export const FAQS = [];

/**
 * Header configuration — populated by the inline visual editor via the
 * backend's `header_config` field. Empty strings/arrays fall back to
 * baked-in defaults inside `Header.jsx`.
 */
export const HEADER = {
  logo_text: "",
  logo_image_url: "",
  show_phone: true,
  cta_text: "Book Consultation",
  cta_link: "/consultations",
  cta_open_new_tab: false,
  nav_items: [],
};

/**
 * Footer configuration — populated by the inline visual editor via the
 * backend's `footer_config` field.
 */
export const FOOTER = {
  tagline: "",
  copyright: "",
  socials: [],
  quick_links: [],
  newsletter_enabled: false,
  newsletter_heading: "",
  newsletter_subtitle: "",
  newsletter_cta_text: "Subscribe",
  show_contact: true,
  show_address: true,
};

/**
 * Brand palette — surfaced via the Site Settings modal.
 */
export const BRANDING = {
  primary: "",
  accent: "",
  neutral: "",
};

/**
 * Per-element style/advanced overrides — keyed by anchor path or testid.
 * Populated by the inline visual editor's Style / Advanced tabs. Consumed
 * via the `withElStyle` / `elClassName` helpers below.
 */
export const ELEMENT_STYLES = {};
export const ELEMENT_ADVANCED = {};

/** Runtime context set by the shell (not part of the saved config). */
export const RUNTIME = { slug: "" };

/**
 * Convert a style-map entry into a Tailwind-safe `{ style, className }`
 * pair. Falls through to `existing` when no override is set for the key.
 */
export function withElStyle(key, existing = {}) {
  const s = ELEMENT_STYLES[key];
  const adv = ELEMENT_ADVANCED[key];
  if (!s && !adv) return existing;

  const style = { ...(existing.style || {}) };
  const extraClasses = [];

  if (s) {
    if (s.background_color) style.backgroundColor = s.background_color;
    if (s.text_color) style.color = s.text_color;
    if (s.text_align) style.textAlign = s.text_align;
    // Padding is expressed as Tailwind override utilities so it wins even
    // when the template already sets a padding class. `!important` prefix
    // ensures we beat the base rule.
    const paddingClass = { sm: "!py-8", md: "!py-16", lg: "!py-24" }[s.padding];
    if (paddingClass) extraClasses.push(paddingClass);
  }

  if (adv && adv.css_class) extraClasses.push(adv.css_class);

  const className = extraClasses.length
    ? [existing.className, ...extraClasses].filter(Boolean).join(" ")
    : existing.className;

  const idProp = adv && adv.anchor_id ? { id: adv.anchor_id } : {};

  return { ...existing, ...idProp, className, style };
}

/**
 * Per-page, per-section text overrides. Every field is nullable — the
 * template pages read `COPY.<page>.<key> || DEFAULT` so any missing entry
 * falls back to the built-in template copy (no visual break).
 *
 * Backend builds this from summary + AI content in `_build_service_config`.
 */
export const COPY = {
  home: {
    hero: { eyebrow: "", title: "", subtitle: "", trustLine: "" },
    servicesOverview: { eyebrow: "", title: "", subtitle: "" },
    consultation: { eyebrow: "", title: "", subtitle: "", bullets: [] },
    testimonials: { eyebrow: "", title: "" },
    serviceArea: {},
    finalCta: { title: "", subtitle: "" },
    // Iter-75: keys allow WhyChooseUs.jsx + ProcessSteps.jsx to read
    // override values from the backend copy tree (fed by
    // `website_content.copy_overrides`). Empty defaults ensure the
    // component's built-in text still shows when there's no override.
    whyChooseUs: { eyebrow: "", title: "", subtitle: "" },
    process: { eyebrow: "", title: "", subtitle: "" },
  },
  services: {
    hero: { eyebrow: "", title: "", subtitle: "" },
    detail: {},
    finalCta: { title: "", subtitle: "" },
  },
  about: {
    hero: { eyebrow: "", title: "", subtitle: "" },
    story: { eyebrow: "", title: "", paragraphs: [] },
    values: { eyebrow: "", title: "", items: [] },
    trust: { eyebrow: "", title: "", stats: [] },
    finalCta: { title: "", subtitle: "" },
  },
  consultations: {
    hero: { eyebrow: "", title: "", subtitle: "", trustLine: "" },
    expect: { eyebrow: "", title: "", subtitle: "", items: [] },
    finalCta: { eyebrow: "", title: "", subtitle: "" },
  },
  reviews: {
    hero: { eyebrow: "", title: "", subtitle: "" },
    section: { eyebrow: "", title: "" },
    stats: {},
    finalCta: { title: "", subtitle: "" },
  },
  faq: {
    hero: { eyebrow: "", title: "", subtitle: "" },
    finalCta: { title: "", subtitle: "" },
  },
  contact: {
    hero: { eyebrow: "", title: "", subtitle: "" },
    section: { eyebrow: "", title: "" },
    info: {},
    hours: {},
    finalCta: { title: "", subtitle: "" },
  },
  // Iter-77 — visible form copy (labels/placeholders/submit/footnotes) and
  // site-wide shared strings. Populated exclusively via copy_overrides.
  forms: {
    consultation: {},
    contact: {},
  },
  shared: { ctaCallPrefix: "" },
  brand: { footerBlurb: "" },
};

export const TRUST_POINTS = [
  { label: "Locally Owned", icon: "MapPin" },
  { label: "Licensed & Insured", icon: "ShieldCheck" },
  { label: "Free Consultations", icon: "MessageSquare" },
  { label: "Fast Response", icon: "Zap" },
  { label: "Satisfaction Guaranteed", icon: "BadgeCheck" },
];

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Tell us what you need",
    body: "Call, text, or fill out the form. Describe the project in your own words — no jargon required.",
  },
  {
    number: "02",
    title: "Get a consultation or quote",
    body: "We visit (or hop on a quick call), diagnose the real issue, and send a written, itemized quote.",
  },
  {
    number: "03",
    title: "Schedule your service",
    body: "Pick a day that works. We show up on time, do the work right, and clean up before we leave.",
  },
];

export const WHY_US = [
  {
    title: "Clear communication",
    body: "Real people, real replies. Text or call — we answer within the hour, every hour we're open.",
    icon: "MessageCircle",
  },
  {
    title: "Honest pricing",
    body: "Flat-rate quotes in writing before any work starts. The price we quote is the price you pay.",
    icon: "ReceiptText",
  },
  {
    title: "Reliable service",
    body: "On-time arrival windows, and if we're running late you'll know first. No ghosting, ever.",
    icon: "Clock",
  },
  {
    title: "Local experience",
    body: "Owner-operated in your community. We know the codes, the neighborhoods, and the weather.",
    icon: "Home",
  },
  {
    title: "Easy booking",
    body: "Book online in under two minutes or call and talk to a human. Whatever works for you.",
    icon: "CalendarCheck",
  },
  {
    title: "Professional results",
    body: "Licensed, insured, background-checked technicians. Workmanship warranty on every job.",
    icon: "Award",
  },
];

// ---------- Runtime data injector ----------
/** Mutate the exported arrays/objects in place. Called by the shell BEFORE
 * mounting the template so page components see real data on first render. */
export function setServiceData(config) {
  if (!config || typeof config !== "object") return;

  // SITE — replace keys we know about, keep sane defaults for anything missing.
  if (config.site && typeof config.site === "object") {
    // Wipe old keys so we don't carry over stale values from a previous site.
    for (const k of Object.keys(SITE)) delete SITE[k];
    Object.assign(SITE, config.site);
  }

  // IMAGES
  if (config.images && typeof config.images === "object") {
    for (const k of Object.keys(IMAGES)) IMAGES[k] = "";
    Object.assign(IMAGES, config.images);
  }

  // Arrays — splice-in-place so identity is preserved for any consumer that
  // captured a reference at import time.
  const replaceArr = (target, next) => {
    target.length = 0;
    if (Array.isArray(next)) for (const item of next) target.push(item);
  };
  if (Array.isArray(config.services)) replaceArr(SERVICES, config.services);
  if (Array.isArray(config.testimonials)) replaceArr(TESTIMONIALS, config.testimonials);
  if (Array.isArray(config.faqs)) replaceArr(FAQS, config.faqs);
  if (Array.isArray(config.trust_points)) replaceArr(TRUST_POINTS, config.trust_points);
  if (Array.isArray(config.process_steps)) replaceArr(PROCESS_STEPS, config.process_steps);
  if (Array.isArray(config.why_us)) replaceArr(WHY_US, config.why_us);

  // HEADER — dict merge. Reset all keys we know about so stale values from a
  // prior site don't leak in.
  if (config.header && typeof config.header === "object") {
    for (const k of Object.keys(HEADER)) {
      if (Array.isArray(HEADER[k])) HEADER[k].length = 0;
      else if (typeof HEADER[k] === "boolean") HEADER[k] = false;
      else HEADER[k] = "";
    }
    for (const k of Object.keys(config.header)) {
      const v = config.header[k];
      if (Array.isArray(v)) {
        HEADER[k] = HEADER[k] || [];
        if (!Array.isArray(HEADER[k])) HEADER[k] = [];
        HEADER[k].length = 0;
        for (const item of v) HEADER[k].push(item);
      } else {
        HEADER[k] = v;
      }
    }
  }

  if (config.footer && typeof config.footer === "object") {
    for (const k of Object.keys(FOOTER)) {
      if (Array.isArray(FOOTER[k])) FOOTER[k].length = 0;
      else if (typeof FOOTER[k] === "boolean") FOOTER[k] = false;
      else FOOTER[k] = "";
    }
    for (const k of Object.keys(config.footer)) {
      const v = config.footer[k];
      if (Array.isArray(v)) {
        FOOTER[k] = FOOTER[k] || [];
        if (!Array.isArray(FOOTER[k])) FOOTER[k] = [];
        FOOTER[k].length = 0;
        for (const item of v) FOOTER[k].push(item);
      } else {
        FOOTER[k] = v;
      }
    }
  }

  if (config.branding && typeof config.branding === "object") {
    for (const k of Object.keys(BRANDING)) BRANDING[k] = "";
    Object.assign(BRANDING, config.branding);
  }

  // Per-element style / advanced overrides. Same reset pattern used for
  // HEADER/FOOTER — clear the map, then re-hydrate so previous keys don't
  // leak across sites.
  if (config.element_styles && typeof config.element_styles === "object") {
    Object.keys(ELEMENT_STYLES).forEach((k) => delete ELEMENT_STYLES[k]);
    Object.assign(ELEMENT_STYLES, config.element_styles);
  }
  if (config.element_advanced && typeof config.element_advanced === "object") {
    Object.keys(ELEMENT_ADVANCED).forEach((k) => delete ELEMENT_ADVANCED[k]);
    Object.assign(ELEMENT_ADVANCED, config.element_advanced);
  }

  // COPY — deep-merge per page. We iterate the existing COPY skeleton so
  // stray keys can't be injected, and we mutate the sub-objects in place so
  // components that captured a reference (e.g. `const hero = COPY.home.hero`)
  // still see updates.
  if (config.copy && typeof config.copy === "object") {
    for (const pageKey of Object.keys(COPY)) {
      const src = config.copy[pageKey];
      if (!src || typeof src !== "object") continue;
      for (const secKey of Object.keys(COPY[pageKey])) {
        const secSrc = src[secKey];
        if (secSrc === undefined || secSrc === null) continue;
        if (Array.isArray(COPY[pageKey][secKey])) {
          replaceArr(COPY[pageKey][secKey], secSrc);
        } else if (typeof COPY[pageKey][secKey] === "object") {
          // wipe old fields so stale defaults don't leak
          for (const f of Object.keys(COPY[pageKey][secKey])) {
            if (Array.isArray(COPY[pageKey][secKey][f])) {
              COPY[pageKey][secKey][f].length = 0;
            } else {
              COPY[pageKey][secKey][f] = "";
            }
          }
          Object.assign(COPY[pageKey][secKey], secSrc);
        } else {
          COPY[pageKey][secKey] = secSrc;
        }
      }
    }
  }

}